import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ColorHash from 'color-hash';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';

import { Field, Fields } from 'redux-form';

import SelectAutosuggestField from 'components/fields/SelectAutosuggestField';
import AutosuggestField from 'components/fields/AutosuggestField';
import DateRangeFields from 'components/fields/DateRangeFields';

import orderLineTypes from 'config/orderLineTypes';

import { requestSearchUsers } from 'redux/modules/users';
import {
  requestFetchSalesRepresentatives,
  requestSearchSalesRepresentatives,
} from 'redux/modules/salesRepresentatives';
import {
  requestFetchReferralSources,
  requestSearchReferralSources,
} from 'redux/modules/referralSources';
import {
  requestFetchInsurances,
  requestSearchInsurances,
} from 'redux/modules/insurances';
import {
  requestFetchPrescribers,
  requestSearchPrescribers,
} from 'redux/modules/prescribers';
import { accountLoginFail } from 'redux/modules/account';
import formatPrescriberName from 'utils/formatPrescriberName';

const styles = theme => ({
  labelCount: {
    marginLeft: 5,
    color: theme.palette.secondary[450],
  },
  iconColorHash: {
    display: 'inline-block',
    width: 6,
    height: 30,
    margin: '-14px 12px -14px 0',
    borderRadius: 2,
    verticalAlign: 'middle',
    backgroundColor: theme.palette.primary.A100,
  },
  gridItemType: {
    maxWidth: '11%',
    flexBasis: '11%',
    [theme.breakpoints.up('lg')]: {
      maxWidth: '12%',
      flexBasis: '12%',
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '50%',
      flexBasis: '50%',
    },
  },
  gridItemStatus: {
    maxWidth: '10%',
    flexBasis: '10%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '50%',
      flexBasis: '50%',
    },
  },
  gridItemCity: {
    maxWidth: '10%',
    flexBasis: '10%',
  },
  gridItemWatcher: {
    maxWidth: '11%',
    flexBasis: '11%',
  },
  gridItemPrescriber: {
    zIndex: 2,
  },
});

const colorHash = new ColorHash();

const OrdersFilterParameters = ({
  variant,
  className,
  clearFields,
  change,
  selectedStatuses,
  selectedTypes,
  selectedWatcher,
  selectedUsers,
  selectedDrivers,
  selectedPayers,
  selectedSales,
  selectedReferralSources,
  selectedPrescribers,
  contextFiltersOptions,
  actions,
  width,
  classes,
}) => {
  const contextStatuses = contextFiltersOptions
    ? contextFiltersOptions.statuses : [];
  const remainingStatuses = contextStatuses
    .filter(item => selectedStatuses.indexOf(item.ID) === -1);
  const contextOrderLineTypes = contextFiltersOptions
    ? contextFiltersOptions.orderLineTypes : orderLineTypes.map(item => ({ name: item }));
  const remainingOrderLineTypes = contextOrderLineTypes.filter(item =>
    selectedTypes.indexOf(item.name) === -1);
  const remainingUsers = contextFiltersOptions
    ? contextFiltersOptions.users.filter(item => selectedUsers.indexOf(item.ID) === -1)
    : [];
  const remainingDrivers = contextFiltersOptions
    ? contextFiltersOptions.drivers.filter(item => selectedDrivers.indexOf(item.ID) === -1)
    : [];
  const contextWatchers = contextFiltersOptions
    ? contextFiltersOptions.watchers : [];
  const remainingWatchers = contextWatchers
    .filter(item => selectedWatcher.indexOf(item.ID) === -1);
  const remainingInsurances = contextFiltersOptions
    ? contextFiltersOptions.insurances.filter(item => selectedPayers.indexOf(item.ID) === -1)
    : [];
  const remainingSales = contextFiltersOptions
    ? contextFiltersOptions.sales.filter(item => selectedSales.indexOf(item.ID) === -1)
    : [];
  const remainingReferralSources = contextFiltersOptions
    ? contextFiltersOptions.referralSources.filter(item =>
      selectedReferralSources.indexOf(item.ID) === -1)
    : [];
  const remainingPrescribers = contextFiltersOptions
  ? contextFiltersOptions.prescribers.filter(item =>
    selectedPrescribers.indexOf(item.ID) === -1)
  : [];
  const fetchUserSuggestions = contextFiltersOptions && contextFiltersOptions.users
    ? ({ value, offset }) => (new Promise((resolve) => {
      if (offset === 0) {
        if (value.length > 0) {
          resolve(remainingUsers
            .filter(item => `${item['First Name']} ${item['Last Name']}`.toLowerCase().indexOf(value.toLowerCase()) !== -1));
        } else {
          resolve(remainingUsers);
        }
      } else {
        resolve([]);
      }
    }))
    : ({ value, limit, offset }) => (
      value.length > 0
        ? requestSearchUsers({
          filters: [
            {
              name: 'Role',
              operation: 'nin',
              val: ['Driver', 'System'],
            },
            {
              name: 'Full Name',
              operation: 'startswith',
              val: value,
              case_sensitivity: false,
            },
          ],
          limit,
          offset,
        }).then(res => res.data.filter(item => selectedUsers.indexOf(item.ID) === -1),
          (err) => {
            if (err.data.error === 'Need authorisation') {
              actions.accountLoginFail();
            }
            return err;
          })
        : requestSearchUsers({
          filters: [
            {
              name: 'Role',
              operation: 'nin',
              val: ['Driver', 'System'],
            },
          ],
          limit,
          offset,
        }).then(res => res.data.filter(item => selectedUsers.indexOf(item.ID) === -1),
          (err) => {
            if (err.data.error === 'Need authorisation') {
              actions.accountLoginFail();
            }
            return err;
          })
    );
  const fetchDriverSuggestions = contextFiltersOptions && contextFiltersOptions.drivers
    ? ({ value, offset }) => (new Promise((resolve) => {
      if (offset === 0) {
        if (value.length > 0) {
          resolve(remainingDrivers
            .filter(item => `${item['First Name']} ${item['Last Name']}`.toLowerCase().indexOf(value.toLowerCase()) !== -1));
        } else {
          resolve(remainingDrivers);
        }
      } else {
        resolve([]);
      }
    }))
    : ({ value, limit, offset }) => (
      value.length > 0
        ? requestSearchUsers({
          filters: [
            {
              name: 'Full Name',
              operation: 'startswith',
              val: value,
              case_sensitivity: false,
            },
            {
              name: 'Role',
              operation: 'eq',
              val: 'Driver',
            },
          ],
          limit,
          offset,
        }).then(res => res.data.filter(item => selectedDrivers.indexOf(item.ID) === -1),
          (err) => {
            if (err.data.error === 'Need authorisation') {
              actions.accountLoginFail();
            }
            return err;
          })
        : requestSearchUsers({
          filters: [
            {
              name: 'Role',
              operation: 'eq',
              val: 'Driver',
            },
          ],
          limit,
          offset,
        }).then(res => res.data.filter(item => selectedDrivers.indexOf(item.ID) === -1),
          (err) => {
            if (err.data.error === 'Need authorisation') {
              actions.accountLoginFail();
            }
            return err;
          })
    );
  const fetchInsuranceSuggestions = contextFiltersOptions && contextFiltersOptions.insurances
    ? ({ value, offset }) => (new Promise((resolve) => {
      if (offset === 0) {
        if (value.length > 0) {
          resolve(remainingInsurances
            .filter(item => item['Payer Name']
              && item['Payer Name'].toLowerCase().indexOf(value.toLowerCase()) !== -1));
        } else {
          resolve(remainingInsurances);
        }
      } else {
        resolve([]);
      }
    }))
    : ({ value, limit, offset }) => (
      value.length > 0
        ? requestSearchInsurances({
          filters: [
            {
              name: 'Payer Name',
              operation: 'startswith',
              val: value,
              case_sensitivity: false,
            },
          ],
          limit,
          offset,
        }).then(res => res.data.filter(item => selectedPayers.indexOf(item.ID) === -1),
          (err) => {
            if (err.data.error === 'Need authorisation') {
              actions.accountLoginFail();
            }
            return err;
          })
        : requestFetchInsurances({
          limit,
          offset,
        }).then(res => res.data.filter(item => selectedPayers.indexOf(item.ID) === -1),
          (err) => {
            if (err.data.error === 'Need authorisation') {
              actions.accountLoginFail();
            }
            return err;
          })
    );
  const fetchSalesSuggestions = contextFiltersOptions && contextFiltersOptions.sales
    ? ({ value, offset }) => (new Promise((resolve) => {
      if (offset === 0) {
        if (value.length > 0) {
          resolve(remainingSales
            .filter(item => `${item['First Name']} ${item['Last Name']}`.toLowerCase().indexOf(value.toLowerCase()) !== -1));
        } else {
          resolve(remainingSales);
        }
      } else {
        resolve([]);
      }
    }))
    : ({ value, limit, offset }) => (
      value.length > 0
        ? requestSearchSalesRepresentatives({
          filters: [
            {
              name: 'Full Name',
              operation: 'startswith',
              val: value,
              case_sensitivity: false,
            },
          ],
          limit,
          offset,
        }).then(res => res.data.filter(item => selectedSales.indexOf(item.ID) === -1),
          (err) => {
            if (err.data.error === 'Need authorisation') {
              actions.accountLoginFail();
            }
            return err;
          })
        : requestFetchSalesRepresentatives({
          limit,
          offset,
        }).then(res => res.data.filter(item => selectedSales.indexOf(item.ID) === -1),
          (err) => {
            if (err.data.error === 'Need authorisation') {
              actions.accountLoginFail();
            }
            return err;
          })
    );
  const fetchReferralSourcesSuggestions = contextFiltersOptions
    && contextFiltersOptions.referralSources
    ? ({ value, offset }) => (new Promise((resolve) => {
      if (offset === 0) {
        if (value.length > 0) {
          resolve(remainingReferralSources
            .filter(item => item.name.toLowerCase().indexOf(value.toLowerCase()) !== -1));
        } else {
          resolve(remainingReferralSources);
        }
      } else {
        resolve([]);
      }
    }))
    : ({ value, limit, offset }) => (
      value.length > 0
        ? requestSearchReferralSources({
          filters: [
            {
              name: 'Name of source',
              operation: 'startswith',
              val: value,
              case_sensitivity: false,
            },
          ],
          limit,
          offset,
        }).then(res => res.data.filter(item => selectedSales.indexOf(item.ID) === -1),
          (err) => {
            if (err.data.error === 'Need authorisation') {
              actions.accountLoginFail();
            }
            return err;
          })
        : requestFetchReferralSources({
          limit,
          offset,
        }).then(res => res.data.filter(item => selectedSales.indexOf(item.ID) === -1),
          (err) => {
            if (err.data.error === 'Need authorisation') {
              actions.accountLoginFail();
            }
            return err;
          })
    );
    const fetchPrescriberSuggestions = contextFiltersOptions && contextFiltersOptions.sales
    ? ({ value, offset }) => (new Promise((resolve) => {
      if (offset === 0) {
        if (value.length > 0) {
          resolve(remainingPrescribers
            .filter(item => formatPrescriberName(item).toLowerCase().indexOf(value.toLowerCase()) !== -1));
        } else {
          resolve(remainingPrescribers);
        }
      } else {
        resolve([]);
      }
    }))
    : ({ value, limit, offset }) => (
      value.length > 0
        ? requestSearchPrescribers({
          filters: [
            {
              name: 'Full Name',
              operation: 'startswith',
              val: value,
              case_sensitivity: false,
            },
          ],
          limit,
          offset,
        }).then(res => res.data.filter(item => selectedPrescribers.indexOf(item.ID) === -1),
          (err) => {
            if (err.data.error === 'Need authorisation') {
              actions.accountLoginFail();
            }
            return err;
          })
        : requestFetchPrescribers({
          limit,
          offset,
        }).then(res => res.data.filter(item => selectedPrescribers.indexOf(item.ID) === -1),
          (err) => {
            if (err.data.error === 'Need authorisation') {
              actions.accountLoginFail();
            }
            return err;
          })
    );

  const fetchWatcherSuggestions = ({ value, offset }) => (new Promise((resolve) => {
      if (offset === 0) {
        if (value.length > 0) {
          resolve(remainingWatchers
            .filter(item => `${item['First Name']} ${item['Last Name']}`.toLowerCase().indexOf(value.toLowerCase()) !== -1));
        } else {
          resolve(remainingWatchers);
        }
      } else {
        resolve([]);
      }
    }));

  return (
    <Grid
      className={className}
      container
      justify="flex-start"
      alignItems="center"
      spacing={16}
      wrap={width === 'xs' || width === 'sm' ? 'wrap' : 'nowrap'}
    >
      <Grid item>
        <Fields
          id="Date of Creation"
          names={[
            'Date of Service From',
            'Date of Service To',
            'Date of Creation From',
            'Date of Creation To',
            'Date of Delivery From',
            'Date of Delivery To',
            'Date of Claim Submission From',
            'Date of Claim Submission To',
          ]}
          placeholder="Date"
          component={DateRangeFields}
          fullWidth
          size="lg"
        />
      </Grid>
      <Grid item>
        <Field
          id="User Name"
          name="User Name"
          component={SelectAutosuggestField}
          size="lg"
          placeholder="User"
          alwaysRenderSuggestions
          fullWidth
          disabled={remainingUsers.length === 0}
          renderSuggestionContent={suggestion => (
            <div>
              <span>{`${suggestion['First Name']} ${suggestion['Last Name']}`}</span>
              {(suggestion.count && variant !== 'patient') &&
                <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
              }
            </div>
          )}
          renderInputContent={suggestion => (
            `${suggestion['First Name']} ${suggestion['Last Name']}`
          )}
          onSuggestionSelected={(suggestion) => {
            clearFields(false, false, 'User Name');
            change('User', [{
              value: suggestion.ID,
              label: `${suggestion['First Name']} ${suggestion['Last Name']}`,
            }]);
            return '';
          }}
          fetchSuggestions={fetchUserSuggestions}
          keepOpenAfterSelection
          onListAdornmentClick={() => {
            change('User', remainingUsers.map(item => ({
              value: item.ID,
              label: `${item['First Name']} ${item['Last Name']}`,
            })));
          }}
          listAdornmentText="Select All"
        />
      </Grid>
      {variant !== 'patient' &&
        <Grid item>
          <Field
            name="Watcher Name"
            id="Watcher Name"
            autosuggestFieldName="Watcher Name"
            fieldArrayName="Watcher"
            placeholder="Watcher"
            component={SelectAutosuggestField}
            renderSuggestionContent={suggestion => (
              <div>
                <span>{`${suggestion['First Name']} ${suggestion['Last Name']}`}</span>
                {(suggestion.count && variant !== 'patient') &&
                  <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
                }
              </div>
            )}
            renderInputContent={suggestion => `${suggestion['First Name']} ${suggestion['Last Name']}`}
            onSuggestionSelected={(suggestion) => {
              clearFields(false, false, 'Watcher Name');
              change('Watcher', [{
                value: suggestion.ID,
                label: `${suggestion['First Name']} ${suggestion['Last Name']}`,
              }]);
              return '';
            }}
            fetchSuggestions={fetchWatcherSuggestions}
            keepOpenAfterSelection
            listWidth="auto"
          />
        </Grid>
      }
      <Grid
        className={classNames(classes.gridItemStatus)}
        item
        xs={3}
      >
        <Field
          id="Status Name"
          name="Status Name"
          placeholder="Status"
          component={AutosuggestField}
          fullWidth
          disabled={remainingStatuses.length === 0}
          renderSuggestionContent={suggestion => (
            <div>
              <span>{suggestion.name}</span>
              {(suggestion.count && variant !== 'patient') &&
                <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
              }
            </div>
          )}
          renderInputContent={suggestion => suggestion.name}
          onSuggestionSelected={(suggestion) => {
            change('Status', [{
              value: suggestion.ID,
              label: suggestion.name,
            }]);
            return '';
          }}
          fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
            if (offset === 0) {
              if (value.length > 0) {
                resolve(remainingStatuses
                  .filter(item => item.name.toLowerCase().indexOf(value.toLowerCase()) !== -1));
              } else {
                resolve(remainingStatuses);
              }
            } else {
              resolve([]);
            }
          }))}
          listWidth="auto"
          onlyConfirmedSelection
          clearSearchValueAfterSelection
          keepOpenAfterSelection
          onListAdornmentClick={() => {
            change('Status', remainingStatuses.map(item => ({
              value: item.ID,
              label: item.name,
            })));
          }}
          listAdornmentText="Select All"
        />
      </Grid>
      <Grid
        className={classes.gridItemType}
        item
      >
        <Field
          id="Type Name"
          name="Type Name"
          placeholder="Type"
          component={AutosuggestField}
          fullWidth
          listWidth="auto"
          disabled={remainingOrderLineTypes.length === 0}
          renderSuggestionContent={suggestion => (
            <div>
              <span>{suggestion.name}</span>
              {(suggestion.count && variant !== 'patient') &&
                <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
              }
            </div>
          )}
          renderInputContent={suggestion => suggestion.name}
          onSuggestionSelected={(suggestion) => {
            change('Type', [{
              value: suggestion.name,
              label: suggestion.name,
            }]);
            return '';
          }}
          fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
            if (offset === 0) {
              if (value.length > 0) {
                resolve(remainingOrderLineTypes
                  .filter(item => item.name.toLowerCase().indexOf(value.toLowerCase()) !== -1));
              } else {
                resolve(remainingOrderLineTypes);
              }
            } else {
              resolve([]);
            }
          }))}
          onlyConfirmedSelection
          clearSearchValueAfterSelection
          keepOpenAfterSelection
          onListAdornmentClick={() => {
            change('Type', remainingOrderLineTypes.map(item => ({
              value: item.name,
              label: item.name,
            })));
          }}
          listAdornmentText="Select All"
        />
      </Grid>
      <Grid item>
        <Field
          id="Driver Name"
          name="Driver Name"
          component={SelectAutosuggestField}
          size="lg"
          placeholder="Driver"
          alwaysRenderSuggestions
          fullWidth
          disabled={remainingDrivers.length === 0}
          {...variant === 'schedule' ? {
            renderSuggestionContent: suggestion => (
              <div>
                {suggestion.ID !== null &&
                  <div
                    className={classes.iconColorHash}
                    style={{ backgroundColor: colorHash.hex(`00000${suggestion.ID}`.slice(-6)) }}
                  />
                }
                <span>{`${suggestion['First Name']} ${suggestion['Last Name']}`}</span>
                {(suggestion.count && variant !== 'patient') &&
                  <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
                }
              </div>
            ),
          } : {
            renderSuggestionContent: suggestion => (
              <div>
                <span>{`${suggestion['First Name']} ${suggestion['Last Name']}`}</span>
                {(suggestion.count && variant !== 'patient') &&
                  <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
                }
              </div>
            ),
          }}
          renderInputContent={suggestion => (
            `${suggestion['First Name']} ${suggestion['Last Name']}`
          )}
          getSuggestionValue={suggestion => (
            `${suggestion['First Name']} ${suggestion['Last Name']}`
          )}
          onSuggestionSelected={(suggestion) => {
            clearFields(false, false, 'Driver Name');
            change('Driver', [{
              value: suggestion.ID,
              label: `${suggestion['First Name']} ${suggestion['Last Name']}`,
            }]);
          }}
          fetchSuggestions={fetchDriverSuggestions}
          keepOpenAfterSelection
          onListAdornmentClick={() => {
            change('Driver', remainingDrivers.map(item => ({
              value: item.ID,
              label: `${item['First Name']} ${item['Last Name']}`,
            })));
          }}
          listAdornmentText="Select All"
        />
      </Grid>
      <Grid item>
        <Field
          id="Payer Name"
          name="Payer Name"
          component={SelectAutosuggestField}
          size="lg"
          placeholder="Payer"
          alwaysRenderSuggestions
          fullWidth
          disabled={remainingInsurances.length === 0}
          renderSuggestionContent={suggestion => (
            <div>
              <span>{suggestion['Payer Name']}</span>
              {(suggestion.count && variant !== 'patient') &&
                <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
              }
            </div>
          )}
          renderInputContent={suggestion => suggestion['Payer Name']}
          getSuggestionValue={suggestion => suggestion['Payer Name']}
          onSuggestionSelected={(suggestion) => {
            change('Payer', [{
              value: suggestion.ID,
              label: suggestion['Payer Name'],
            }]);
            return '';
          }}
          fetchSuggestions={fetchInsuranceSuggestions}
          placement="bottom-right"
          onlyConfirmedSelection
          clearSearchValueAfterSelection
          keepOpenAfterSelection
          onListAdornmentClick={() => {
            change('Payer', remainingInsurances.map(item => ({
              value: item.ID,
              label: item['Payer Name'],
            })));
          }}
          listAdornmentText="Select All"
        />
      </Grid>
      <Grid item>
        <Field
          id="Sales Name"
          name="Sales Name"
          component={SelectAutosuggestField}
          size="lg"
          placeholder="Sales"
          alwaysRenderSuggestions
          fullWidth
          disabled={remainingSales.length === 0}
          renderSuggestionContent={suggestion => (
            <div>
              <span>{`${suggestion['First Name']} ${suggestion['Last Name']}`}</span>
              {(suggestion.count && variant !== 'patient') &&
                <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
              }
            </div>
          )}
          renderInputContent={suggestion => `${suggestion['First Name']} ${suggestion['Last Name']}`}
          getSuggestionValue={suggestion => `${suggestion['First Name']} ${suggestion['Last Name']}`}
          onSuggestionSelected={(suggestion) => {
            clearFields(false, false, 'Sales Name');
            change('Sales', [{
              value: suggestion.ID,
              label: `${suggestion['First Name']} ${suggestion['Last Name']}`,
            }]);
          }}
          fetchSuggestions={fetchSalesSuggestions}
          placement="bottom-right"
          onlyConfirmedSelection
          clearSearchValueAfterSelection
          keepOpenAfterSelection
          onListAdornmentClick={() => {
            change('Sales', remainingSales.map(item => ({
              value: item.ID,
              label: `${item['First Name']} ${item['Last Name']}`,
            })));
          }}
          listAdornmentText="Select All"
        />
      </Grid>
      <Grid item>
        <Field
          id="Referral Name"
          name="Referral Name"
          component={SelectAutosuggestField}
          size="lg"
          placeholder="Referral"
          alwaysRenderSuggestions
          fullWidth
          disabled={remainingReferralSources.length === 0}
          renderSuggestionContent={suggestion => (
            <div>
              <span>{suggestion.name || suggestion['Name of source']}</span>
              {(suggestion.count && variant !== 'patient') &&
                <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
              }
            </div>
          )}
          renderInputContent={suggestion => suggestion.name || suggestion['Name of source']}
          getSuggestionValue={suggestion => suggestion.name || suggestion['Name of source']}
          onSuggestionSelected={(suggestion) => {
            clearFields(false, false, 'Referral Name');
            change('Referral', [{
              value: suggestion.ID,
              label: suggestion.name || suggestion['Name of source'],
            }]);
          }}
          fetchSuggestions={fetchReferralSourcesSuggestions}
          placement="bottom-right"
          keepOpenAfterSelection
          onListAdornmentClick={() => {
            change('Referral', remainingReferralSources.map(item => ({
              value: item.ID,
              label: item.name || item['Name of source'],
            })));
          }}
          listAdornmentText="Select All"
        />
      </Grid>
      <Grid item className={classes.gridItemPrescriber}>
        <Field
          id="Prescriber Name"
          name="Prescriber Name"
          component={SelectAutosuggestField}
          size="lg"
          placeholder="Prescriber"
          alwaysRenderSuggestions
          fullWidth
          disabled={remainingPrescribers.length === 0}
          renderSuggestionContent={suggestion => (
            <div>
              <span>{formatPrescriberName(suggestion)}</span>
              {(suggestion.count && variant !== 'patient') &&
                <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
              }
            </div>
          )}
          renderInputContent={suggestion => formatPrescriberName(suggestion)}
          getSuggestionValue={suggestion => formatPrescriberName(suggestion)}
          onSuggestionSelected={(suggestion) => {
            clearFields(false, false, 'Prescriber Name');
            change('Prescriber', [{
              value: suggestion.ID,
              label: formatPrescriberName(suggestion),
            }]);
          }}
          fetchSuggestions={fetchPrescriberSuggestions}
          placement="bottom-right"
          keepOpenAfterSelection
          onListAdornmentClick={() => {
            change('Prescriber', remainingPrescribers.map(item => ({
              value: item.ID,
              label: formatPrescriberName(item),
            })));
          }}
          listAdornmentText="Select All"
        />
      </Grid>
    </Grid>
  );
};

OrdersFilterParameters.propTypes = {
  variant: PropTypes.oneOf([
    'workflow',
    'schedule',
    'patient',
  ]).isRequired,
  selectedStatuses: PropTypes.arrayOf(PropTypes.number),
  selectedTypes: PropTypes.arrayOf(PropTypes.string),
  selectedCities: PropTypes.arrayOf(PropTypes.string),
  selectedUsers: PropTypes.arrayOf(PropTypes.number),
  selectedDrivers: PropTypes.arrayOf(PropTypes.number),
  selectedPayers: PropTypes.arrayOf(PropTypes.number),
  selectedSales: PropTypes.arrayOf(PropTypes.number),
  selectedReferralSources: PropTypes.arrayOf(PropTypes.number),
  selectedPrescribers: PropTypes.arrayOf(PropTypes.number),
  className: PropTypes.string,
  pristine: PropTypes.bool.isRequired,
  reset: PropTypes.func.isRequired,
  clearFields: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  contextFiltersOptions: PropTypes.shape({
    statuses: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      count: PropTypes.number,
    })).isRequired,
    orderLineTypes: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      count: PropTypes.number,
    })).isRequired,
  }),
  actions: PropTypes.shape({
    accountLoginFail: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    labelCount: PropTypes.string.isRequired,
    iconColorHash: PropTypes.string.isRequired,
    gridItemType: PropTypes.string.isRequired,
    gridItemStatus: PropTypes.string.isRequired,
    gridItemCity: PropTypes.string.isRequired,
  }).isRequired,
};

OrdersFilterParameters.defaultProps = {
  className: '',
  selectedStatuses: [],
  selectedTypes: [],
  selectedCities: [],
  selectedUsers: [],
  selectedDrivers: [],
  selectedPayers: [],
  selectedSales: [],
  selectedReferralSources: [],
  selectedPrescribers: [],
  contextFiltersOptions: null,
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ accountLoginFail }, dispatch),
});

export default connect(null, mapDispatchToProps)(
  withStyles(styles)(withWidth()(OrdersFilterParameters)),
);



// WEBPACK FOOTER //
// ./src/components/workflow/OrdersFilter/OrdersFilterParameters.js